import I18n from 'I18n';
import { FilterBar } from 'foundations-components/transitional/components/FilterBar';
import { useCallback, useContext, useMemo, useState } from 'react';
import ChartTypeToggleDropdown from 'report-customization-components/components/chart-type-toggle-dropdown/ChartTypeToggleDropdown';
import { isConfigReport } from 'reporting-data/tsTypes/reportTypes';
import ErrorBoundaryDecorator from 'reporting-ui-components/reports/decorators/ErrorBoundaryDecorator';
import { EnablementTrackingContext } from '../../monitoring/EnablementTrackingContext';
import useGetIsReportValidForLiteEdittingPanel from '../singleReportViewer/common/sidebar/useGetIsReportValidForLiteEdittingPanel';
import { SingleObjectOvertimeTotalsEditor, strategy as overtimeTotalsStrategy } from './OvertimeTotalsEditor/SingleObjectOvertimeTotalsEditor';
import { ResetReportButton } from './common/ResetReportButton';
import { useEditors } from './common/useEditorsAndFilters';
import { useShouldShowFiltersBar } from './common/useShouldShowFiltersBar';
import { isSearchReport } from 'reporting-data/report/configReportGetters';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const useShouldShowEditorsBarInSRV = ({
  report
}) => {
  const {
    showFiltersBar
  } = useShouldShowFiltersBar(report);
  const hasControls = overtimeTotalsStrategy.isSupportedForReport(report);
  return showFiltersBar && (!isConfigReport(report) || !isSearchReport(report)) && hasControls;
};
const SRVEditorsBar = ({
  appUsageTracker,
  report,
  onPreviewReportChange,
  resetReportEdits,
  condensedFullpageView = false,
  hideChartTypeToggle
}) => {
  const [reportChanged, setReportChanged] = useState(false);
  const isValidForLiteEditingPanel = useGetIsReportValidForLiteEdittingPanel(report);
  const {
    trackingApplicationName,
    trackingScreenName,
    trackingAssetType
  } = useContext(EnablementTrackingContext);
  const options = useMemo(() => ({
    readOnly: false
  }), []);
  const handlePreviewReportChange = useCallback(updatedReport => {
    onPreviewReportChange(updatedReport);
    setReportChanged(true);
  }, [onPreviewReportChange]);
  const handleResetReportEdits = useCallback(() => {
    if (resetReportEdits) {
      resetReportEdits();
      setReportChanged(false);
    }
  }, [resetReportEdits]);
  const editors = useEditors(report);
  const showResetButton = !isValidForLiteEditingPanel && !condensedFullpageView && resetReportEdits;
  const showEditors = !isValidForLiteEditingPanel && !condensedFullpageView && editors.length > 0;
  const showFilterBar = !hideChartTypeToggle || showEditors || showResetButton;
  const showOvertimeTotals = overtimeTotalsStrategy.isSupportedForReport(report);
  if (!showFilterBar && !showOvertimeTotals) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "reporting-editor-bar",
    children: [showOvertimeTotals && /*#__PURE__*/_jsx(SingleObjectOvertimeTotalsEditor, {
      report: report,
      onChange: handlePreviewReportChange,
      appUsageTracker: appUsageTracker,
      trackingAssetType: trackingAssetType,
      trackingApplicationName: trackingApplicationName,
      trackingScreenName: trackingScreenName
    }), showFilterBar && /*#__PURE__*/_jsx(FilterBar, {
      marginBlockEnd: condensedFullpageView ? '0' : undefined,
      paddingBlockEnd: condensedFullpageView ? undefined : '100',
      "data-test-id": "reporting-filter-bar",
      startSlot: /*#__PURE__*/_jsxs(_Fragment, {
        children: [!hideChartTypeToggle && /*#__PURE__*/_jsx(ChartTypeToggleDropdown, {
          appUsageTracker: appUsageTracker,
          onChange: handlePreviewReportChange,
          report: report
        }), showEditors && editors.map(Editor => /*#__PURE__*/_jsx(Editor, {
          appUsageTracker: appUsageTracker,
          isV2FilterExperience: true,
          onChange: handlePreviewReportChange,
          onPreviewReportChange: handlePreviewReportChange,
          options: options,
          report: report,
          trackingAssetType: trackingAssetType,
          trackingApplicationName: trackingApplicationName,
          trackingScreenName: trackingScreenName
        }, Editor.displayName || Editor.name))]
      }),
      endSlot: showResetButton && /*#__PURE__*/_jsx(ResetReportButton, {
        disabled: !reportChanged,
        onReset: handleResetReportEdits,
        tooltipText: I18n.text('reporting-enablement.reportEditors.reset.tooltip.editors')
      })
    })]
  });
};
export default ErrorBoundaryDecorator(SRVEditorsBar, 'SRVEditorsBar');